import { Button } from "@egaranti/components";
import { Input } from "@egaranti/components";
import { Label } from "@egaranti/components";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@egaranti/components";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@egaranti/components";

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import SearchSelect from "@/components/extendedWarranty/add/searchSelect";
import DateRangePicker from "@/components/ui/dateRangePicker";

import employeesService from "@/services/employeesService";

import SubMerchantService from "@/services/subMerchantService";

import useExtendedWarrantyStore from "@/stores/useExtendedWarranty";

import useMerchantUserType from "@/hooks/useMerchantUserType";

import { format, parse } from "date-fns";

import {
  ACTIVE_SALES_FILTER,
  ALL_SALES_FILTER,
  SALE_STATUS_FILTERS,
  getSaleStatusPresentationByKey,
} from "@/lib/saleStatus";

const ALL_STORES = "ALL";
const DATE_FORMAT = "yyyy-MM-dd";

const toLocalDate = (value) =>
  value ? parse(value, DATE_FORMAT, new Date()) : undefined;

// EPS-5027: tek "Bayi" kutusunda iki farkli kavram listeleniyor — ayni merchant
// icindeki STORE kullanicilari (merchantUserId) ve main_merchant_id ile bagli ayri
// merchant'lar, yani alt bayiler (subMerchantId). Secimin hangisi oldugu anahtardaki
// onekten anlasiliyor.
const USER_KEY_PREFIX = "user:";
const MERCHANT_KEY_PREFIX = "merchant:";

// EPS-5115: statu secenekleri SALE_STATUS_FILTERS sirasiyla listelenir; her secenegin
// hangi kaynaga (taslak/satis) nasil gittigi orada. "Tumu" ve "Aktif Satislar" disindaki
// seceneklerin etiketi listedeki statu rozetiyle ayni anahtardan gelir — filtredeki ad ile
// listedeki etiket hep ayni kalsin diye.
const STATUS_OPTION_LABELS = {
  [ALL_SALES_FILTER]: ["extendedWarrantiesPage.status.allSales", "Tümü"],
  [ACTIVE_SALES_FILTER]: [
    "extendedWarrantiesPage.status.activeSalesFilter",
    "Aktif Satışlar",
  ],
};

// Varsayilan statu "Tumu" — temizlenen filtre de buraya doner.
const emptyFilters = {
  firstName: "",
  lastName: "",
  merchantUserId: "",
  subMerchantId: "",
  saleStatus: ALL_SALES_FILTER,
  createdAtGreaterThan: "",
  createdAtLessThan: "",
};

const FilterSheet = ({ onOpenChange, open }) => {
  const { t } = useTranslation();
  const { filters, getExtendedWarranties, setFilters } =
    useExtendedWarrantyStore();
  const { isStoreUser } = useMerchantUserType();

  const [filtersInSheet, setFiltersInSheet] = useState(emptyFilters);
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [subMerchants, setSubMerchants] = useState([]);

  // Sheet her açıldığında aktif filtrelerle senkronla — aksi halde kapatıp açınca
  // ekranda görünen değerler uygulanan filtrelerden sapıyor.
  useEffect(() => {
    if (!open) return;
    setFiltersInSheet({
      firstName: filters.firstName || "",
      lastName: filters.lastName || "",
      merchantUserId: filters.merchantUserId || "",
      subMerchantId: filters.subMerchantId || "",
      saleStatus: filters.saleStatus || ALL_SALES_FILTER,
      createdAtGreaterThan: filters.createdAtGreaterThan || "",
      createdAtLessThan: filters.createdAtLessThan || "",
    });
  }, [open]);

  // EPS-4974: bayi listesi yalnızca ana merchant için anlamlı; alt bayi zaten
  // kendi satışlarını görüyor.
  useEffect(() => {
    if (!open || isStoreUser || stores.length) return;
    setStoresLoading(true);
    employeesService
      .getMerchantUsers()
      .then((users) =>
        setStores((users || []).filter((user) => user.type === "STORE")),
      )
      .catch(() => setStores([]))
      .finally(() => setStoresLoading(false));
  }, [open, isStoreUser]);

  // EPS-5027: ana bayinin kendisine bağlı alt bayileri. Alt bayi kullanıcısında
  // (STORE) ve alt bayisi olmayan merchant'ta liste boş döner, kutuya bir şey eklenmez.
  useEffect(() => {
    if (!open || isStoreUser || subMerchants.length) return;
    SubMerchantService.getSubMerchants()
      .then(setSubMerchants)
      .catch(() => setSubMerchants([]));
  }, [open, isStoreUser]);

  // Bayi sayısı yüzleri bulduğu için liste aranabilir olmak zorunda; arama hem
  // ada hem bayi koduna bakabilsin diye ikisi de tek etikette birleştiriliyor.
  const storeItems = useMemo(
    () => [
      {
        key: ALL_STORES,
        name: t("extendedWarrantiesPage.allStores", "Tüm bayiler"),
      },
      ...subMerchants.map((subMerchant) => ({
        key: `${MERCHANT_KEY_PREFIX}${subMerchant.id}`,
        name: `${subMerchant.title} (${t("subMerchantsPage.title", "Alt Bayi")})`,
      })),
      ...stores.map((store) => {
        const fullName = `${store.firstName || ""} ${store.lastName || ""}`.trim();
        return {
          key: `${USER_KEY_PREFIX}${store.id}`,
          name: store.sellerCode ? `${fullName} (${store.sellerCode})` : fullName,
        };
      }),
    ],
    [stores, subMerchants, t],
  );

  const selectedStoreKey = filtersInSheet.subMerchantId
    ? `${MERCHANT_KEY_PREFIX}${filtersInSheet.subMerchantId}`
    : filtersInSheet.merchantUserId
      ? `${USER_KEY_PREFIX}${filtersInSheet.merchantUserId}`
      : ALL_STORES;

  const selectedStoreName =
    storeItems.find((item) => item.key === selectedStoreKey)?.name || "";

  const statusOptions = Object.keys(SALE_STATUS_FILTERS).map((key) => {
    const presentation = getSaleStatusPresentationByKey(key);
    const [labelKey, labelFallback] = STATUS_OPTION_LABELS[key] || [
      presentation.labelKey,
      presentation.labelFallback,
    ];
    return { key, label: t(labelKey, labelFallback) };
  });

  const onSaveFilter = () => {
    onOpenChange(false);
    setFilters({ ...filters, ...filtersInSheet, page: 1 });
    getExtendedWarranties();
  };

  const onClearFilter = () => {
    setFiltersInSheet(emptyFilters);
    setFilters({ ...filters, ...emptyFilters, page: 1 });
    getExtendedWarranties();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="m-4 rounded-lg bg-white p-4">
        <SheetHeader>
          <SheetTitle>{t("common.addFilter")}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 flex flex-col gap-4">
          <Label>{t("extendedWarrantiesPage.firstName")}</Label>
          <Input
            value={filtersInSheet.firstName}
            onChange={(e) =>
              setFiltersInSheet({
                ...filtersInSheet,
                firstName: e.target.value,
              })
            }
          />
          <Label>{t("extendedWarrantiesPage.lastName")}</Label>
          <Input
            value={filtersInSheet.lastName}
            onChange={(e) =>
              setFiltersInSheet({
                ...filtersInSheet,
                lastName: e.target.value,
              })
            }
          />
          <Label>{t("extendedWarrantiesPage.status.title", "Statü")}</Label>
          <Select
            value={filtersInSheet.saleStatus}
            onValueChange={(value) =>
              setFiltersInSheet({ ...filtersInSheet, saleStatus: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map(({ key, label }) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label>
            {t("extendedWarrantiesPage.saleDateRange", "Satış Tarih Aralığı")}
          </Label>
          <DateRangePicker
            dateRange={
              filtersInSheet.createdAtGreaterThan
                ? {
                    from: toLocalDate(filtersInSheet.createdAtGreaterThan),
                    to: toLocalDate(filtersInSheet.createdAtLessThan),
                  }
                : undefined
            }
            onChange={(range) =>
              setFiltersInSheet({
                ...filtersInSheet,
                createdAtGreaterThan: range?.from
                  ? format(range.from, DATE_FORMAT)
                  : "",
                createdAtLessThan: range?.from
                  ? format(range.to || range.from, DATE_FORMAT)
                  : "",
              })
            }
          />
          {!isStoreUser &&
            (storesLoading || stores.length > 0 || subMerchants.length > 0) && (
              <>
                <Label>{t("extendedWarrantiesPage.store", "Bayi")}</Label>
                <SearchSelect
                  value={selectedStoreName}
                  items={storeItems}
                  loading={storesLoading}
                  placeholder={t(
                    "extendedWarrantiesPage.searchStore",
                    "Bayi ara...",
                  )}
                  ariaLabel={t("extendedWarrantiesPage.store", "Bayi")}
                  onSelect={(item) =>
                    setFiltersInSheet({
                      ...filtersInSheet,
                      // İki alan birbirini dışlar: bayi/personel seçimi merchantUserId,
                      // alt bayi seçimi subMerchantId olarak gider.
                      merchantUserId: item.key.startsWith(USER_KEY_PREFIX)
                        ? item.key.slice(USER_KEY_PREFIX.length)
                        : "",
                      subMerchantId: item.key.startsWith(MERCHANT_KEY_PREFIX)
                        ? item.key.slice(MERCHANT_KEY_PREFIX.length)
                        : "",
                    })
                  }
                />
              </>
            )}
        </div>
        <SheetFooter className="mt-4 flex gap-2">
          <Button variant="secondaryGray" onClick={onClearFilter}>
            {t("common.clear", "Temizle")}
          </Button>
          <Button onClick={onSaveFilter}>{t("common.save")}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default FilterSheet;
